import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHead, Err, Empty, useRecords } from '../ui.js';
import {
  startBuild, getBuildJob, listSuggestions, markSuggestionAdded, dismissSuggestion,
  revealContact, ApiError,
} from '../api.js';
import type { BuildJob, ContactSuggestion, CompanyCard } from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// BUILD THE LIST — the walk across accounts, and the people it turns up.
//
// The page is in two halves because the operator's two questions are: "go find
// the people" and "which of these are worth a credit". Everything above the
// results is about starting a run; everything below is one decision per person.
//
// WHAT COSTS MONEY IS SAID BEFORE IT IS SPENT, and the paid button is
// deliberately not the warm one. Firmographics are a credit per account and can
// be switched off; the people search is free; revealing an address is a credit
// and stays one click on one person, which is the whole reason this screen does
// not have a "reveal all" button. See build-core.ts.
//
// WHY SURNAMES LOOK BROKEN. Apollo returns "Riley L." until someone pays to
// reveal the record. Shown as returned rather than tidied — a screen that
// prettied it up would be implying it knows a name it has not bought.
// ─────────────────────────────────────────────────────────────────────────────

const LIVE = ['queued', 'running'];

export function Build() {
  const [params] = useSearchParams();
  const companies = useRecords<CompanyCard & { id: string; slug: string; name: string }>('companies');

  const [picked, setPicked] = useState<Set<string>>(new Set());
  // OFF BY DEFAULT (2026-08-26). Kyle's rule for this screen is that nothing
  // costs money until he commits, and the people search — the reason anybody
  // opens it — is free. Firmographics are a credit PER ACCOUNT, so leaving this
  // ticked meant the obvious first action on a list of twenty-five accounts was
  // a twenty-five credit purchase of data he had not asked for and might never
  // read. The box is still here, one click away, and the cost line beside the
  // button names the price the moment it is ticked.
  const [enrich, setEnrich] = useState(false);
  const [job, setJob] = useState<BuildJob | null>(null);
  const [rows, setRows] = useState<ContactSuggestion[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  // Arriving from Research with the accounts just promoted, or from Accounts
  // with a selection. The handoff is the point: re-selecting the same ten here
  // is the click count this removes.
  //
  // `enrich=1` ONLY pre-ticks the paid box, and only because the button that
  // sent you here said "Enrich and find people" in as many words. An expressed
  // intention is not the same thing as a default, which is why the default is
  // still off — and nothing is bought until the run button on this screen.
  useEffect(() => {
    const s = params.get('slugs');
    if (s) setPicked(new Set(s.split(',').filter(Boolean)));
    if (params.get('enrich') === '1') setEnrich(true);
  }, [params]);

  const load = useCallback(() => {
    listSuggestions().then((r) => setRows(r.suggestions)).catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!job || !LIVE.includes(job.status)) {
      if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
      return;
    }
    timer.current = window.setInterval(() => {
      getBuildJob(job.id)
        .then((r) => { setJob(r.job); if (r.job.status === 'done') load(); })
        .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
    }, 3000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [job?.id, job?.status, load]);

  const all = companies.rows ?? [];
  const toggle = (slug: string) => setPicked((p) => {
    const n = new Set(p);
    if (n.has(slug)) n.delete(slug); else n.add(slug);
    return n;
  });

  async function run() {
    setBusy('run'); setError(null);
    try {
      const r = await startBuild([...picked], enrich);
      setJob({
        id: r.job_id, status: 'queued', progress: 'Starting…', found: 0, error: null,
        searches: 0, in_tokens: 0, out_tokens: 0,
        created_at: new Date().toISOString(), finished_at: null, output: {},
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(null); }
  }

  // One person, one credit, one click. Reveal writes the contact through the
  // existing audited enrichment route; this only records the decision.
  async function reveal(s: ContactSuggestion) {
    if (!s.apollo_person_id) { setError(`No Apollo id for ${s.name}.`); return; }
    setBusy(s.id); setError(null);
    try {
      const r = await revealContact(s.company_slug, s.apollo_person_id);
      await markSuggestionAdded(s.id, (r.contact as { id?: string })?.id ?? null);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(null); }
  }

  async function drop(s: ContactSuggestion) {
    setBusy(s.id); setError(null);
    try { await dismissSuggestion(s.id); load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : String(e)); }
    finally { setBusy(null); }
  }

  const live = job && LIVE.includes(job.status);
  const byCompany = new Map<string, ContactSuggestion[]>();
  for (const r of rows) {
    const k = `${r.company_name}||${r.company_slug}`;
    if (!byCompany.has(k)) byCompany.set(k, []);
    byCompany.get(k)!.push(r);
  }

  // Only unenriched accounts cost anything — the walk skips ones already bought.
  const cost = enrich ? [...picked].length : 0;

  return (
    <div className="build">
      <PageHead
        title="Build the list"
        sub="Pick the accounts, and it finds who works there. It stages people; you decide who is worth an address."
      />

      <Err>{error}</Err>

      <section className="panel">
        <h4>Accounts</h4>
        {all.length === 0 && (
          <p className="meta">
            No accounts yet. <Link to="/research">Find some first.</Link>
          </p>
        )}
        {all.length > 0 && (
          <>
            <div className="pickgrid">
              {all.map((c) => (
                <label key={c.slug} className={`pickitem ${picked.has(c.slug) ? 'on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={picked.has(c.slug)}
                    onChange={() => toggle(c.slug)}
                  />
                  <span>{c.name}</span>
                </label>
              ))}
            </div>

            <div className="toolbar">
              <label className="check">
                <input type="checkbox" checked={enrich}
                  onChange={(e) => setEnrich(e.target.checked)} />
                Also buy firmographics <span className="meta">(1 credit per account)</span>
              </label>
              <button disabled={busy === 'run' || picked.size === 0 || Boolean(live)}
                onClick={() => void run()}>
                {busy === 'run' ? 'Starting…'
                  : `Find people at ${picked.size} account${picked.size === 1 ? '' : 's'}`}
              </button>
              {/* Spend is visible, before it is spent, and never on the warm button. */}
              <span className="cost">
                {cost > 0
                  ? `up to ${cost} Apollo credit${cost === 1 ? '' : 's'} · people search is free`
                  : 'free — people search only'}
              </span>
            </div>
          </>
        )}
      </section>

      {live && (
        <p className="meta running">{job.progress ?? 'Working…'}</p>
      )}
      {job && job.status === 'failed' && <Err>{job.error ?? 'The run failed.'}</Err>}
      {job && job.status === 'done' && (
        <p className="notice">
          {job.output.enriched ?? 0} enriched · {job.output.suggested ?? 0} people staged
          {typeof job.output.credits === 'number' && ` · ${job.output.credits} credits spent`}
          {/* NO SILENT SKIPS. An account the walk could not touch has to say so,
              or the run reads as complete coverage when it was not. */}
          {job.output.skipped && job.output.skipped.length > 0 && (
            <><br /><span className="meta">Skipped — {job.output.skipped.join(' · ')}</span></>
          )}
        </p>
      )}

      {rows.length === 0 && !live && (
        <Empty
          title="Nobody staged"
          hint="Run the finder above. People land here with a title and no address until you spend a credit on one."
        />
      )}

      {[...byCompany.entries()].map(([key, people]) => {
        const [name, slug] = key.split('||');
        return (
          <section className="panel" key={key}>
            <h4>
              <Link to={`/companies/${slug}`}>{name}</Link>
              <span className="meta"> · {people.length} found</span>
            </h4>
            <ul className="peoplelist">
              {people.map((s) => (
                <li key={s.id}>
                  <div className="qbody">
                    <div className="qline">
                      <strong>{s.name}</strong>
                      {s.seniority && <span className="flag flag-mute">{s.seniority}</span>}
                      {s.raw?.has_email === false && (
                        <span className="flag flag-warn">no address on file</span>
                      )}
                    </div>
                    <span className="qwhy">
                      {s.title ?? 'title unknown'}
                      {s.linkedin_url && (
                        <> · <a href={s.linkedin_url} target="_blank" rel="noreferrer">LinkedIn</a></>
                      )}
                    </span>
                  </div>
                  <button disabled={busy === s.id || !s.apollo_person_id}
                    title="Costs one Apollo credit"
                    onClick={() => void reveal(s)}>
                    {busy === s.id ? 'Revealing…' : 'Reveal — 1 credit'}
                  </button>
                  <button className="qsnooze" disabled={busy === s.id}
                    onClick={() => void drop(s)}>Dismiss</button>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
